import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import { Slot } from '@radix-ui/react-slot';
import {
  textDefaultElement,
  textLinkRecipe,
  textRecipe,
  type TextLinkRecipeProps,
  type TextRecipeProps,
} from '../recipes/text.recipe';

type TextElement = React.ElementType;

export interface TextProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'>,
    TextRecipeProps {
  /**
   * Override the rendered element. Each variant already picks a sensible
   * default (`h2` renders an <h2>, `body` renders a <p>), so reach for this
   * only when the semantics and the styling genuinely differ — a subheading
   * that must not be an <h3> in the document outline, for example.
   */
  as?: TextElement;
}

/**
 * Every piece of text in the app.
 *
 * Instead of `className="text-2xl font-semibold tracking-tight"` scattered
 * across screens, pick the role: `<Text variant="h2">`. When the type scale is
 * retuned in tokens.css, every heading of that rank moves with it.
 */
export const Text = React.forwardRef<HTMLElement, TextProps>(function Text(
  {
    as,
    variant,
    tone,
    weight,
    align,
    truncate,
    numeric,
    preserveLines,
    className,
    ...props
  },
  ref
) {
  const Component = (as ??
    textDefaultElement[variant ?? 'body']) as React.ElementType;

  /*
   * `variant="inherit"` means inherit — including colour, unless a tone is
   * named. The recipe's default tone is `default`, which paints
   * `text-foreground`; applied to an inline run inside muted text that would
   * silently recolour it, turning "make these two words bold" into a visual
   * change nobody asked for.
   */
  const resolvedTone = tone ?? (variant === 'inherit' ? 'inherit' : undefined);

  return (
    <Component
      ref={ref}
      className={cn(
        textRecipe({
          variant,
          tone: resolvedTone,
          weight,
          align,
          truncate,
          numeric,
          preserveLines,
        }),
        className
      )}
      {...props}
    />
  );
});

export interface HeadingProps extends Omit<TextProps, 'variant'> {
  /**
   * Visual rank. Maps to the matching heading element unless `as` says
   * otherwise, so the document outline follows the visual hierarchy.
   */
  level?: 1 | 2 | 3 | 4;
}

export interface TextLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    TextLinkRecipeProps {
  /**
   * Hand rendering to the child — a router `<Link>`:
   *
   *   <TextLink asChild><Link to="/signup">Sign up</Link></TextLink>
   */
  asChild?: boolean;
  /**
   * Render a real `<button type="button">` when the "link" runs code rather
   * than going somewhere — "Not you?", "send it again". Still looks like a
   * link, is still a button to the keyboard and a screen reader, and keeps a
   * raw `<button>` out of the page:
   *
   *   <TextLink as="button" onClick={handleNotYou}>Not you?</TextLink>
   */
  as?: 'a' | 'button';
  /**
   * Button form only: the action is unavailable for the moment — "send it
   * again" while the first send is still in flight. Ignored on an anchor.
   */
  disabled?: boolean;
  /** Opens in a new tab, with the rel that makes that safe. */
  external?: boolean;
}

/**
 * A link inside running text.
 *
 *   <Text variant="body-sm" tone="muted">
 *     Forgot your password? <TextLink href="/reset">Reset it</TextLink>
 *   </Text>
 *
 * Inherits its size from the sentence it sits in. Button's `link` variant is
 * the other thing — a control that happens to look like a link, with a height
 * and a place in a button row.
 */
export const TextLink = React.forwardRef<HTMLAnchorElement, TextLinkProps>(
  function TextLink(
    {
      asChild = false,
      as = 'a',
      external = false,
      disabled,
      tone,
      className,
      ...props
    },
    ref
  ) {
    const classes = cn(textLinkRecipe({ tone }), className);
    if (!asChild && as === 'button') {
      const buttonRef = ref as unknown as React.Ref<HTMLButtonElement>;
      return (
        <button
          ref={buttonRef}
          type="button"
          disabled={disabled}
          className={classes}
          {...(props as unknown as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        />
      );
    }
    const Comp = asChild ? Slot : 'a';
    const externalProps = external
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : {};
    return <Comp ref={ref} className={classes} {...externalProps} {...props} />;
  }
);

/**
 * "Open in Google Maps" for a place we only have as a GPS point. Opens in a
 * new tab, and a click on it does not also click the table row it sits in.
 *
 *   <OpenInMapsLink href="https://www.google.com/maps/search/?api=1&query=37.35,-121.95" />
 */
export function OpenInMapsLink({ href }: { href: string }) {
  return (
    <TextLink href={href} external onClick={(e) => e.stopPropagation()}>
      Open in Google Maps
    </TextLink>
  );
}
